<script setup>
// PublicLayout — the SIGNED-OUT shell. A storefront, not an application.
//
// The public surface used to reuse ConsumerShell (saas-core's ConsumerLayout:
// sidebar + account nav + tab strip), which told a first-time visitor "you are
// inside a tool you haven't signed up for yet". This is a separate shell so the
// two audiences can diverge without conditional CSS piling up in one layout —
// and, critically, so the tenant surface CANNOT regress: ConsumerShell is
// untouched and still serves every signed-in route.
//
// Brand tokens are shared with the deck card (navy #0b1730, gold #e8b54d) but
// the PAGE stays quiet: the cards already carry dark overlays, so a dark page
// would make the whole thing feel heavy and cost the cards their contrast.
import { ref, computed } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useBuild } from "../../composables/useBuild.js";

const route = useRoute();
const menuOpen = ref(false);

// The header is OVERLAID on the home page's hero artwork (absolute + transparent),
// so it costs no vertical space there. Every OTHER page in this shell — the static
// pages and the anonymous player — has no hero to overlay, so it needs the header's
// height back as top padding, or its content would slide under the logo.
const hasHero = computed(() => route.path === "/");
const year = new Date().getFullYear();
// Build stamp in the legal footer line (2026-09-14) — the same text the player
// shows under its Talk controls, so "which build is this?" is answerable from
// the front page too.
const { label: buildLabel } = useBuild();

// The CURRENT logo. `logo.jpg` is the pre-rename "Recall Master" artwork and must
// not be used anywhere — this is the same asset ConsumerShell serves, so the two
// surfaces can't drift apart. It carries an alpha channel, which is what lets the
// footer invert it to white cleanly (an opaque source would invert to a box).
//
// BOUND, not a literal src: the file is served by Express from public/images at
// RUNTIME and isn't inside Vite's build root — a static src="/images/..." makes
// Vite try to bundle it and the build fails with UNRESOLVED_IMPORT.
const LOGO = "/images/i-recall-logo.png";

const NAV = [
  { label: "Explore topics", to: "/" },
  { label: "How it works", to: "/how-it-works" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" }
];

// NOTE: the value-prop strip used to live HERE, after <router-view />. Because
// PublicHome has two root elements, router-view renders a fragment and the strip
// landed *inside* it (between the hero and the catalogue) rather than after it —
// the right place by accident, not by instruction. It now lives in PublicHome,
// explicitly positioned, so its placement is controlled rather than emergent.
</script>

<template>
  <div class="pub">
    <!-- Header ------------------------------------------------------------ -->
    <header class="pub-head">
      <div class="pub-wrap pub-head-in">
        <RouterLink to="/" class="pub-logo" aria-label="i-Recall home">
          <img :src="LOGO" alt="i-Recall" />
        </RouterLink>

        <nav class="pub-nav" :class="{ open: menuOpen }" aria-label="Primary">
          <RouterLink v-for="n in NAV" :key="n.to" :to="n.to" class="pub-nav-l" @click="menuOpen = false">
            {{ n.label }}
          </RouterLink>
          <div class="pub-nav-cta">
            <RouterLink to="/login" class="pub-signin" @click="menuOpen = false">Sign in</RouterLink>
            <RouterLink to="/register" class="pub-join" @click="menuOpen = false">Create free account</RouterLink>
          </div>
        </nav>

        <button
          class="pub-burger" type="button" aria-label="Menu"
          :aria-expanded="menuOpen" @click="menuOpen = !menuOpen">
          <i class="bi" :class="menuOpen ? 'bi-x-lg' : 'bi-list'"></i>
        </button>
      </div>
    </header>

    <main class="pub-main" :class="{ 'no-hero': !hasHero }">
      <router-view />
    </main>

    <!-- Footer ------------------------------------------------------------ -->
    <footer class="pub-foot">
      <div class="pub-wrap pub-foot-in">
        <div class="pub-foot-brand">
          <img :src="LOGO" alt="i-Recall" />
          <p>Speak it. Remember it. Master it.</p>
        </div>

        <div class="pub-foot-cols">
          <div>
            <h4>Explore</h4>
            <RouterLink to="/">Topics</RouterLink>
            <RouterLink to="/how-it-works">How it works</RouterLink>
          </div>
          <div>
            <h4>Account</h4>
            <RouterLink to="/login">Sign in</RouterLink>
            <RouterLink to="/register">Create free account</RouterLink>
          </div>
          <div>
            <h4>Company</h4>
            <RouterLink to="/about">About i-Recall</RouterLink>
            <RouterLink to="/contact">Contact</RouterLink>
          </div>
        </div>
      </div>

      <div class="pub-wrap pub-foot-legal">
        <span>© {{ year }} i-Recall. All rights reserved.<span v-if="buildLabel" class="pub-build" title="Build stamp (Major.YY.MMDD.HHMM UTC) · git commit"> · {{ buildLabel }}</span></span>
        <nav>
          <RouterLink to="/privacy">Privacy Policy</RouterLink>
          <RouterLink to="/terms">Terms of Service</RouterLink>
        </nav>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.pub { min-height: 100vh; display: flex; flex-direction: column; background: #fbfcfe; color: #0f172a; position: relative; }
.pub-wrap { width: 100%; max-width: 1180px; margin: 0 auto; padding: 0 1.25rem; }
.pub-build { opacity: .7; font-variant-numeric: tabular-nums; white-space: nowrap; }

/* --- header ---
   OVERLAID on the hero artwork, not stacked above it: a 100px logo in its own
   opaque bar cost ~120px of vertical space before the page even began. Absolute +
   transparent means the logo and menu ride ON the image and that band is
   reclaimed. The artwork's left/top is near-white and its copy zone is clear, so
   the dark nav text keeps its contrast.
   Pages with no hero (Pricing, About…) pad their own top to clear it — see
   PublicPage.vue. */
.pub-head { position: absolute; top: 0; left: 0; right: 0; z-index: 50; background: transparent; }
.pub-head-in { display: flex; align-items: center; gap: 1.5rem; padding: .4rem 1.25rem; position: relative; }
.pub-logo img { height: 100px; width: auto; display: block; }
/* Pulled up so the menu sits level with the TOP of the logo rather than its
   optical centre — the logo is a tall wordmark, and centring the nav against it
   pushed the nav down into the artwork's busiest band. */
.pub-nav { margin-left: auto; display: flex; align-items: center; gap: 1.75rem; margin-top: -65px; }
.pub-nav-l { color: #334155; text-decoration: none; font-size: 1rem; font-weight: 500; white-space: nowrap; }
.pub-nav-l:hover, .pub-nav-l.router-link-active { color: #2563eb; }
.pub-nav-cta { display: flex; align-items: center; gap: 1.25rem; margin-left: .75rem; }
.pub-signin { color: #0f172a; text-decoration: none; font-weight: 600; white-space: nowrap; }
.pub-signin:hover { color: #2563eb; }
.pub-join { background: #2563eb; color: #fff; text-decoration: none; font-weight: 600; white-space: nowrap;
  padding: .7rem 1.25rem; border-radius: 10px; box-shadow: 0 2px 10px rgba(37,99,235,.28); }
.pub-join:hover { background: #1d4ed8; color: #fff; }
.pub-burger { display: none; margin-left: auto; background: none; border: 0; font-size: 1.6rem; color: #0f172a; }

/* --- main --- */
.pub-main { flex: 1; margin-bottom: 40px; }
/* No hero to overlay → give the header's height back, or the content renders
   underneath the logo. The home page pads inside its own hero instead. */
.pub-main.no-hero { padding-top: 116px; }
/* (the value-prop strip's styles moved to PublicHome with the markup) */

/* --- footer --- */
.pub-foot { background: #0b1730; color: #cbd5e1; padding: 3rem 0 1.25rem; }
.pub-foot-in { display: flex; gap: 3rem; flex-wrap: wrap; padding-bottom: 2.5rem; }
.pub-foot-brand { flex: 1 1 240px; }
.pub-foot-brand img { height: 40px; filter: brightness(0) invert(1); opacity: .95; }
.pub-foot-brand p { margin: .9rem 0 0; color: #94a3b8; font-size: .92rem; }
.pub-foot-cols { flex: 2 1 480px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
.pub-foot-cols h4 { font-size: .82rem; text-transform: uppercase; letter-spacing: .08em; color: #fff; margin: 0 0 .9rem; }
.pub-foot-cols a { display: block; color: #94a3b8; text-decoration: none; font-size: .93rem; margin-bottom: .55rem; }
.pub-foot-cols a:hover { color: #fff; }
.pub-foot-legal { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap;
  border-top: 1px solid rgba(255,255,255,.1); padding-top: 1.25rem; font-size: .85rem; color: #7f8ea6; }
.pub-foot-legal nav { display: flex; gap: 1.5rem; }
.pub-foot-legal a { color: #7f8ea6; text-decoration: none; }
.pub-foot-legal a:hover { color: #fff; }

/* --- responsive --- */
@media (max-width: 767.98px) {
  /* A 100px logo eats a phone's screen. Scale it back where space is scarce. */
  .pub-logo img { height: 64px; }
  .pub-main.no-hero { padding-top: 78px; }
  /* The open menu needs an opaque panel — it's now hanging over artwork. */
  .pub-burger { display: block; }
  /* Reset the desktop pull-up: on mobile the nav is a dropdown panel anchored to
     the header, and -65px would hang it up over the logo. */
  .pub-nav { display: none; margin-top: 0; }
  .pub-nav.open {
    display: flex; flex-direction: column; align-items: stretch; gap: .25rem;
    /* Anchored to the header, not a hard-coded 74px — the bar's height now
       follows the logo, so a magic number would leave the menu floating. */
    position: absolute; top: 100%; left: 0; right: 0; background: #fff;
    border-bottom: 1px solid #e8edf5; padding: 1rem 1.25rem 1.4rem; box-shadow: 0 12px 24px rgba(15,23,42,.08);
  }
  .pub-nav-l { padding: .7rem 0; border-bottom: 1px solid #f1f5f9; }
  .pub-nav-cta { flex-direction: column; align-items: stretch; gap: .7rem; margin: .9rem 0 0; }
  .pub-signin, .pub-join { text-align: center; padding: .8rem 1rem; }
  .pub-signin { border: 1px solid #e2e8f0; border-radius: 10px; }
  .pub-foot-cols { grid-template-columns: 1fr 1fr; }
}
</style>
